/**
 * Shaping for the Tracking page's Live Ops tab (get_ops_health RPC).
 *
 * The SLA is CLOCK time: an RFQ that arrives in office hours (Mon-Fri
 * 09:00-18:00 Singapore) should get its first reply within 4 hours of
 * arriving. An RFQ that arrives outside office hours waits for the morning;
 * its 4 hours start at the next 09:00 opening, and until then it is listed as
 * after-hours backlog, not as late. Public holidays are not excluded, so an
 * RFQ waiting over a holiday shows as older - the safe direction for a to-do
 * list. Working minutes (businessMinutesBetween) are kept only for the
 * ingest-stall check, where "how long was the office open" is the question.
 */

import { formatClock, type DrillSpec } from '../analytics/owner/ownerData';

export interface OpenRfq {
  rfq_id: number;
  status: 'NEW' | 'DIY';
  client_name: string | null;
  client_email: string | null;
  /** Email domain with known aliases merged (owner_customer_alias); absent from older RPC versions. */
  customer_key?: string;
  /** Wrist, Seven Seas or ARC Marine: customers that quote mostly through their own portal (owner_is_portal_customer). */
  portal?: boolean;
  /**
   * Company name (20261027160000_dashboard_followups.sql): the Analytics name, else the
   * name on the customer's own purchase orders, else its email domain; null when the
   * sender is unknown. Absent before that migration.
   */
  customer_name?: string | null;
  subject: string | null;
  received_at: string;
  moved_to_manual_at: string | null;
  item_count: number;
}

export interface FailureItem {
  kind: 'rfq_parse_failed' | 'looks_like_rfq_no_items' | 'excel_job_partial' | 'excel_job_stuck';
  at: string;
  subject: string | null;
  sender: string | null;
  detail: string | null;
  rfq_id: number | null;
  rfq_status: string | null;
  attempts: number;
}

interface ClockSpeed {
  answered: number;
  median_minutes: number | null;
  pct_within_1h: number | null;
  pct_within_4h: number | null;
}

interface HourCount {
  hour: number;
  n: number;
}

export type FailureKind = FailureItem['kind'];

export interface OpsHealth {
  generated_at: string;
  ingest: {
    /** RFQs in the system that arrived today: live rows only, so RFQs already deleted in the app are not in it. */
    rfqs_today: number;
    rfqs_last_24h: number;
    /** Distinct emails read as an RFQ today (History's rfq_emails_read for today). */
    rfq_emails_read_today?: number;
    last_rfq_received_at: string | null;
    last_email_processed_at: string | null;
    /** The Sent-folder monitor's own last write; null when it has none on record. */
    last_sent_folder_sync_at: string | null;
    median_rfqs_per_weekday_28d: number | null;
  };
  open_rfqs: {
    count: number;
    manual_count: number;
    /** Split of `count` (status NEW): portal customers vs everyone else. */
    open_email?: number;
    open_portal?: number;
    items: OpenRfq[];
  };
  sla: {
    scanned_through: string | null;
    unmatched_mailbox_threads_14d: number;
    /** Clock time from arrival to first reply, office-hours arrivals. */
    clock: {
      target_minutes: number;
      last_7d: ClockSpeed;
      today: ClockSpeed;
      daily: Array<{ day: string; office_rfqs: number; speed: ClockSpeed }>;
    };
  };
  today: {
    received_by_hour: HourCount[];
    system_sent_by_hour: HourCount[];
    manual_replies_by_hour: HourCount[];
    system_sent: number;
    manual_replies: number;
  };
  /** Newest 25 per kind; failure_counts has the uncapped totals. */
  failures: { items: FailureItem[] };
  failure_counts?: Record<FailureKind | 'total', number>;
}

const SGT_OFFSET_MS = 8 * 60 * 60 * 1000; // Singapore has no DST
const DAY_MS = 24 * 60 * 60 * 1000;
const OPEN_MIN = 9 * 60;
const CLOSE_MIN = 18 * 60;

/** Minutes of Mon-Fri 09:00-18:00 Singapore time between two instants. */
export function businessMinutesBetween(start: Date, end: Date): number {
  const from = start.getTime() + SGT_OFFSET_MS;
  const to = end.getTime() + SGT_OFFSET_MS;
  if (!(to > from)) return 0;
  let total = 0;
  for (let dayStart = Math.floor(from / DAY_MS) * DAY_MS; dayStart < to; dayStart += DAY_MS) {
    const weekday = new Date(dayStart).getUTCDay(); // shifted clock: UTC fields read as SGT
    if (weekday === 0 || weekday === 6) continue;
    const open = dayStart + OPEN_MIN * 60000;
    const close = dayStart + CLOSE_MIN * 60000;
    const overlap = Math.min(close, to) - Math.max(open, from);
    if (overlap > 0) total += overlap / 60000;
  }
  return Math.floor(total);
}

export type SlaState = 'within' | 'due_soon' | 'overdue';

/** Where a waiting RFQ stands against the SLA: due soon from 75% of it. */
export function slaState(waitedBusinessMinutes: number, targetMinutes: number): SlaState {
  if (waitedBusinessMinutes >= targetMinutes) return 'overdue';
  if (waitedBusinessMinutes >= targetMinutes * 0.75) return 'due_soon';
  return 'within';
}

export interface IngestStatus {
  level: 'ok' | 'warning';
  message: string;
}

const isBusinessHours = (now: Date): boolean => businessMinutesBetween(new Date(now.getTime() - 60000), now) > 0;

/**
 * Is mail still being processed? There is no worker heartbeat, so this reads
 * the last processed email against what a normal weekday looks like: during
 * business hours, on a day that usually brings 10+ RFQs, two hours with no
 * email processed is worth checking. Quiet days and nights never warn.
 */
export function ingestStatus(ingest: OpsHealth['ingest'], now: Date, quietLimitMinutes = 120): IngestStatus {
  const last = ingest.last_email_processed_at ? new Date(ingest.last_email_processed_at) : null;
  if (!last) return { level: 'warning', message: 'No processed email on record.' };
  const idleBusiness = businessMinutesBetween(last, now);
  const busyDay = (ingest.median_rfqs_per_weekday_28d ?? 0) >= 10;
  if (isBusinessHours(now) && busyDay && idleBusiness >= quietLimitMinutes) {
    return {
      level: 'warning',
      message: `No email processed for ${formatClock(idleBusiness)} of business time - a normal weekday brings about ${Math.round(ingest.median_rfqs_per_weekday_28d ?? 0)} RFQs. Check the email worker.`,
    };
  }
  return { level: 'ok', message: isBusinessHours(now) ? 'Mail is being processed.' : 'Outside business hours.' };
}

export const FAILURE_LABELS: Record<FailureItem['kind'], string> = {
  rfq_parse_failed: 'RFQ could not be read',
  looks_like_rfq_no_items: 'Reads like an RFQ, no items extracted',
  excel_job_partial: 'Excel fill partly failed',
  excel_job_stuck: 'Excel fill stuck',
};

/** The RFQ list tab an RFQ is listed under; anything unrecognised opens NEW. */
export function rfqListTab(status: string | null): 'NEW' | 'DIY' | 'QUOTED' {
  return status === 'DIY' || status === 'QUOTED' ? status : 'NEW';
}

// ------------------------------------------------------------ clock-time SLA

export const SLA_TARGET_MINUTES = 240;

/** Singapore wall-clock fields of an instant (Singapore has no DST). */
function sgtParts(at: Date): { dow: number; minutes: number; dayStartMs: number } {
  const shifted = at.getTime() + SGT_OFFSET_MS;
  const dayStartMs = Math.floor(shifted / DAY_MS) * DAY_MS;
  return { dow: new Date(dayStartMs).getUTCDay(), minutes: (shifted - dayStartMs) / 60000, dayStartMs };
}

/** Mon-Fri 09:00-18:00 Singapore time. */
export function isOfficeHours(at: Date): boolean {
  const { dow, minutes } = sgtParts(at);
  return dow >= 1 && dow <= 5 && minutes >= OPEN_MIN && minutes < CLOSE_MIN;
}

/** The instant itself inside office hours, else the next weekday 09:00. */
export function nextOpening(at: Date): Date {
  if (isOfficeHours(at)) return at;
  const { dow, minutes, dayStartMs } = sgtParts(at);
  let day = dayStartMs;
  let weekday = dow;
  // Before 09:00 on a weekday opens the same day; anything else moves on a day.
  if (!(weekday >= 1 && weekday <= 5 && minutes < OPEN_MIN)) {
    day += DAY_MS;
    weekday = (weekday + 1) % 7;
  }
  while (weekday === 0 || weekday === 6) {
    day += DAY_MS;
    weekday = (weekday + 1) % 7;
  }
  return new Date(day + OPEN_MIN * 60000 - SGT_OFFSET_MS);
}

export type QueueState = SlaState | 'after_hours';

export interface QueueStatus {
  /** Clock minutes since the RFQ arrived. */
  waited: number;
  /** Clock minutes since its SLA clock started (arrival, or the next opening). */
  slaWaited: number;
  state: QueueState;
  arrivedAfterHours: boolean;
  clockStarts: Date;
}

/**
 * Where a waiting RFQ stands: an office-hours arrival against 4 clock hours
 * from arrival; an after-hours arrival is backlog until the office opens, and
 * its 4 hours run from 09:00.
 */
export function queueStatus(receivedAt: Date, now: Date, targetMinutes = SLA_TARGET_MINUTES): QueueStatus {
  const waited = Math.max(0, Math.floor((now.getTime() - receivedAt.getTime()) / 60000));
  const arrivedAfterHours = !isOfficeHours(receivedAt);
  const clockStarts = nextOpening(receivedAt);
  if (now < clockStarts) return { waited, slaWaited: 0, state: 'after_hours', arrivedAfterHours, clockStarts };
  const slaWaited = Math.floor((now.getTime() - clockStarts.getTime()) / 60000);
  return { waited, slaWaited, state: slaState(slaWaited, targetMinutes), arrivedAfterHours, clockStarts };
}

const AGE_BUCKETS = [
  { key: 'lt1h', label: 'Under 1 h', max: 60 },
  { key: '1to4h', label: '1-4 h', max: 240 },
  { key: '4to24h', label: '4-24 h', max: 1440 },
  { key: '1to3d', label: '1-3 days', max: 4320 },
  { key: 'gt3d', label: 'Over 3 days', max: Infinity },
] as const;

export type AgeBucket = (typeof AGE_BUCKETS)[number]['key'];

/** Clock-age bucket of a wait in minutes. */
export function ageBucket(waitedMinutes: number): AgeBucket {
  return AGE_BUCKETS.find((b) => waitedMinutes < b.max)!.key;
}

/** Open RFQs per clock-age bucket, every bucket present (zeros included). */
export function agingBuckets(waited: number[]): Array<{ key: AgeBucket; label: string; n: number }> {
  return AGE_BUCKETS.map((b) => ({ key: b.key, label: b.label, n: waited.filter((w) => ageBucket(w) === b.key).length }));
}

/** Customer of an open RFQ: the RPC's key, else the email domain. */
export function customerOf(r: Pick<OpenRfq, 'customer_key' | 'client_email'>): string {
  if (r.customer_key) return r.customer_key;
  const m = /@([a-z0-9.-]+[a-z0-9])/i.exec(r.client_email ?? '');
  return m ? m[1].toLowerCase() : 'unknown';
}

/**
 * Fallback company names, used only while get_ops_health does not send
 * customer_name (before 20261027160000_dashboard_followups.sql is applied):
 * the fixed names of owner_customer_label() in 20261026150000_owner_analytics.sql.
 * Other customers then show their email domain: a real identifier, never a
 * guessed name.
 */
const CUSTOMER_NAMES: Record<string, string> = {
  'wrist.com': 'Wrist (Far East / Ship Supply)',
  'fujifts.com': 'Fuji Trading',
  'sevenseasgroup.com': 'Seven Seas Maritime',
  'arcmarine.com': 'ARC Marine',
  'apb.com.sg': 'Asia Pacific Breweries (Heineken)',
  unknown: 'Sender not recorded',
};

/** The company name for a customer: the RPC's customer_name, else the fixed names, else the key itself (an email domain). */
export function customerName(key: string, fromRpc?: string | null): string {
  return fromRpc?.trim() || CUSTOMER_NAMES[key] || key;
}

/**
 * The person an RFQ came from, when the name says more than the address:
 * Wrist's "request" and Fuji's "FTPTECHA" are only the mailbox, so they are
 * not shown as if they were a person.
 */
export function contactName(r: Pick<OpenRfq, 'client_name' | 'client_email'>): string | null {
  const name = (r.client_name ?? '').trim();
  if (!name) return null;
  const email = (r.client_email ?? '').trim().toLowerCase();
  const lower = name.toLowerCase();
  return lower === email || lower === email.split('@')[0] ? null : name;
}

export type QueueChannel = 'all' | 'email' | 'portal';

/** Is an open RFQ in the chosen channel? Portal = Wrist, Seven Seas, ARC Marine. */
export function inChannel(r: Pick<OpenRfq, 'portal'>, channel: QueueChannel): boolean {
  return channel === 'all' || (channel === 'portal') === !!r.portal;
}

export interface CustomerQueue {
  customer: string;
  name: string;
  portal: boolean;
  open: number;
  overdue: number;
  afterHours: number;
  oldest: string;
}

/** Open RFQs grouped by customer, most waiting first. */
export function openByCustomer(
  rows: Array<Pick<OpenRfq, 'customer_key' | 'customer_name' | 'client_email' | 'received_at' | 'portal'> & { state?: QueueState }>,
): CustomerQueue[] {
  const groups = new Map<string, CustomerQueue>();
  for (const r of rows) {
    const key = customerOf(r);
    const g = groups.get(key) ?? { customer: key, name: customerName(key, r.customer_name), portal: !!r.portal, open: 0, overdue: 0, afterHours: 0, oldest: r.received_at };
    g.open += 1;
    if (r.state === 'overdue') g.overdue += 1;
    if (r.state === 'after_hours') g.afterHours += 1;
    if (new Date(r.received_at) < new Date(g.oldest)) g.oldest = r.received_at;
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => b.open - a.open || new Date(a.oldest).getTime() - new Date(b.oldest).getTime());
}

/** Current Singapore hour (0-23). */
export const sgtHour = (now: Date): number => Math.floor(sgtParts(now).minutes / 60);

/** Today's activity by hour from 00:00 to the current hour; empty hours are zero. */
export function hourlyToday(
  today: NonNullable<OpsHealth['today']>,
  lastHour: number,
): Array<{ hour: number; label: string; received: number; system: number; manual: number }> {
  const at = (rows: HourCount[], h: number) => rows.find((r) => r.hour === h)?.n ?? 0;
  const seen = [...today.received_by_hour, ...today.system_sent_by_hour, ...today.manual_replies_by_hour].map((r) => r.hour);
  const top = Math.max(lastHour, ...seen, 0);
  return Array.from({ length: top + 1 }, (_, h) => ({
    hour: h,
    label: `${String(h).padStart(2, '0')}:00`,
    received: at(today.received_by_hour, h),
    system: at(today.system_sent_by_hour, h),
    manual: at(today.manual_replies_by_hour, h),
  }));
}

/**
 * The "Needs a look" count and its breakdown. failure_counts is uncapped;
 * failures.items holds only the newest 25 of each kind, so counting the list
 * understated it (26 shown when 41 were on file). A payload without
 * failure_counts falls back to the list.
 */
export function failureSummary(data: Pick<OpsHealth, 'failures' | 'failure_counts'>): {
  total: number;
  listed: number;
  byKind: Array<{ kind: FailureKind; label: string; n: number; listed: number }>;
} {
  const items = data.failures.items;
  const byKind = (Object.keys(FAILURE_LABELS) as FailureKind[]).map((kind) => {
    const listed = items.filter((f) => f.kind === kind).length;
    return { kind, label: FAILURE_LABELS[kind], n: data.failure_counts?.[kind] ?? listed, listed };
  });
  return { total: data.failure_counts?.total ?? items.length, listed: items.length, byKind };
}

/** '27 Oct 10:49' in Singapore time (24-hour); `never` when there is no value. */
export function whenSgt(iso: string | Date | null | undefined, never = 'never'): string {
  if (!iso) return never;
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return never;
  const date = d.toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date} ${time}`;
}

/** A Singapore calendar date moved by `days`. */
export const shiftDays = (iso: string, days: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * The records behind the Live Ops tiles, each over exactly the window and
 * table its count comes from in get_ops_health. `today` is a Singapore date.
 */
export function opsTileDrills(today: string): Record<'receivedToday' | 'systemQuotesToday' | 'answeredLast7d', DrillSpec> {
  return {
    receivedToday: {
      title: 'RFQs in the system today',
      kind: 'system_rfqs',
      from: today,
      to: today,
      note: 'RFQs the system created from email today (Singapore time) and still holds. RFQs already deleted in the app are not listed.',
    },
    systemQuotesToday: { title: 'Quotes sent from the system today', kind: 'system_quotes', from: today, to: today },
    answeredLast7d: {
      title: 'Office-hours RFQs answered, last 7 days',
      kind: 'rfqs',
      from: shiftDays(today, -6),
      to: today,
      filter: { office: true, answered: true },
      note: 'The last 7 Singapore calendar days including today. RFQs that arrived Mon-Fri 09:00-18:00 and got an email reply; wait is clock time from arrival to first reply.',
    },
  };
}
